// Shared rendering of assistant conversations (RF-UI-11, RF-CHAT-01..04). Each entry says
// what it is: explanatory text, a requested action, an action in progress or a result
// proven by the server (with links). Answers carry their "data as of" moment and refs.
import type { ReactNode } from "react";
import { Link } from "react-router";
import { useI18n } from "../../i18n/index.tsx";
import type { AssistantCommand, AssistantRef, AssistantReply } from "../../api/types-wave2.ts";
import { Pill } from "../../components/Status.tsx";
import { ActionError } from "../../components/ActionError.tsx";
import { Button } from "../../components/Button.tsx";

export type ChatEntry =
  | { kind: "user"; id: string; text: string; command: AssistantCommand | null; at: string }
  | { kind: "pending"; id: string; command: AssistantCommand | null }
  | { kind: "reply"; id: string; reply: AssistantReply; signals: { task_id: string; engine_signal: string }[] }
  | { kind: "history"; id: string; role: string; text: string; at: string }
  | { kind: "cancelled"; id: string; command: AssistantCommand | null; retry: () => void }
  | { kind: "error"; id: string; error: unknown; retry: (() => void) | null };

export interface RefLabeler {
  (ref: AssistantRef): { to: string; label: string } | null;
}

/** Maps API hrefs of the assistant refs to panel routes. */
export function projectRefLink(ref: AssistantRef, projectId: string, taskLabel: (id: string) => string | null, t: (k: "chatRef.task" | "chatRef.board" | "chatRef.project" | "chatRef.usage" | "chatRef.versions" | "chatRef.prds" | "chatRef.prd_version") => string): { to: string; label: string } | null {
  switch (ref.type) {
    case "task":
      return { to: `/projects/${projectId}/kanban?task=${ref.id}`, label: taskLabel(ref.id) ?? t("chatRef.task") };
    case "board":
      return { to: `/projects/${projectId}/kanban`, label: t("chatRef.board") };
    case "project":
      return { to: `/projects/${ref.id}`, label: t("chatRef.project") };
    case "usage":
      return { to: `/projects/${projectId}/runs`, label: t("chatRef.usage") };
    case "versions":
      return { to: `/projects/${projectId}/prds`, label: t("chatRef.versions") };
    case "prds":
      return { to: `/projects/${projectId}/prds`, label: t("chatRef.prds") };
    case "prd_version":
      return { to: `/projects/${projectId}/prds`, label: t("chatRef.prd_version") };
    default:
      return null;
  }
}

function commandLabel(command: AssistantCommand, t: ReturnType<typeof useI18n>["t"]): string {
  if (command.type === "create_task") return t("chat.cmd.createTask", { title: command.task.title });
  if (command.type === "add_guidance") return t("chat.cmd.guidance");
  return t("chat.cmd.importPrd", { title: command.title });
}

export function ChatEntryView({ entry, refLink, avatar }: { entry: ChatEntry; refLink: (ref: AssistantRef) => { to: string; label: string } | null; avatar?: ReactNode }) {
  const { t, formatTime } = useI18n();
  switch (entry.kind) {
    case "user":
      return (
        <li className="chat-msg chat-msg--user" data-testid="chat-user">
          {avatar}
          <div className="chat-bubble">
            {entry.command ? (
              <span className="chat-kind" data-testid="chat-kind">
                <Pill tone="neutral" icon="dot">
                  {t("chat.kind.requested")}
                </Pill>
                <span className="subtle">{commandLabel(entry.command, t)}</span>
              </span>
            ) : null}
            <p className="user-content">{entry.text}</p>
            <span className="chat-time">{formatTime(entry.at)}</span>
          </div>
        </li>
      );
    case "history":
      return (
        <li className={`chat-msg chat-msg--${entry.role === "user" ? "user" : "assistant"}`} data-testid="chat-history">
          <div className="chat-bubble">
            <p className="user-content">{entry.text}</p>
            <span className="chat-time">
              {formatTime(entry.at)} · {t("chat.fromHistory")}
            </span>
          </div>
        </li>
      );
    case "pending":
      return (
        <li className="chat-msg chat-msg--assistant" data-testid="chat-pending" aria-busy="true">
          <div className="chat-bubble">
            <Pill tone="info" icon="spinner">
              {entry.command ? t("chat.kind.inProgress") : t("chat.thinking")}
            </Pill>
          </div>
        </li>
      );
    case "cancelled":
      return (
        <li className="chat-msg chat-msg--assistant" data-testid="chat-cancelled">
          <div className="chat-bubble">
            <p>{t("chat.cancelled")}</p>
            {entry.command ? <p className="field-hint">{t("chat.cancelledCommand")}</p> : null}
            {entry.command ? (
              <Button size="sm" onClick={entry.retry}>
                {t("chat.checkOutcome")}
              </Button>
            ) : null}
          </div>
        </li>
      );
    case "error":
      return (
        <li className="chat-msg chat-msg--assistant" data-testid="chat-error">
          <div className="chat-bubble chat-bubble--wide">
            <ActionError error={entry.error} title={t("chat.failed")} />
            {entry.retry ? (
              <Button size="sm" onClick={entry.retry}>
                {t("common.retry")}
              </Button>
            ) : null}
          </div>
        </li>
      );
    case "reply": {
      const r = entry.reply;
      const proven = r.kind === "command_result";
      const links = r.refs.map((ref) => ({ ref, link: refLink(ref) })).filter((x): x is { ref: AssistantRef; link: { to: string; label: string } } => x.link !== null);
      return (
        <li className="chat-msg chat-msg--assistant" data-testid="chat-reply" data-kind={r.kind}>
          {avatar}
          <div className={`chat-bubble${proven ? " chat-bubble--result" : ""}`}>
            <span className="chat-kind" data-testid="chat-kind">
              {proven ? (
                <Pill tone="success" icon="check">
                  {t("chat.kind.proven")}
                </Pill>
              ) : r.kind === "explanation_unavailable" ? (
                <Pill tone="neutral" icon="circle">
                  {t("chat.kind.explanation")}
                </Pill>
              ) : (
                <Pill tone="info" icon="dot">
                  {t("chat.kind.answer")}
                </Pill>
              )}
            </span>
            <p className="user-content">{r.text}</p>
            {links.length > 0 ? (
              <ul className="chat-refs" aria-label={t("chat.refs")} data-testid="chat-refs">
                {links.map(({ ref, link }) => (
                  <li key={`${ref.type}:${ref.id}`}>
                    <Link to={link.to} className="chat-ref">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            ) : null}
            {entry.signals.length > 0 ? (
              <ul className="plain-list" data-testid="chat-signals">
                {entry.signals.map((s) => (
                  <li key={s.task_id} className={s.engine_signal === "sent" ? "tone-success" : "tone-warning"}>
                    {s.engine_signal === "sent" ? t("chat.signalSent") : t("chat.signalUnavailable")}
                  </li>
                ))}
              </ul>
            ) : null}
            <span className="chat-time" data-testid="chat-data-as-of">
              {t("chat.dataAsOf", { time: formatTime(r.data_as_of.generated_at), cursor: r.data_as_of.event_cursor })} · {t("chat.source", { source: r.source })}
            </span>
          </div>
        </li>
      );
    }
  }
}
